import { ref, computed } from 'vue'
import { initApiService } from '@/services/init'
import { authProvider } from '@/services/currentAuthProvider'
import type { AdminCurrentUserDto } from '@/api/generated-admin'

const adminUser = ref<AdminCurrentUserDto | null>(null)
const isLoading = ref(true)
const isAuthenticated = computed(() => adminUser.value !== null)
const permissions = computed(() => adminUser.value?.permissions ?? [])

export function useAdminAuth() {
  async function fetchAdminUser() {
    try {
      isLoading.value = true
      const apiService = initApiService()
      const response = await apiService.adminAuth.getCurrentAdmin()
      adminUser.value = response.data
    } catch (error) {
      adminUser.value = null
      throw error
    } finally {
      isLoading.value = false
    }
  }

  function hasPermission(permission: string): boolean {
    return permissions.value.includes(permission)
  }

  function hasAnyPermission(...perms: string[]): boolean {
    return perms.some(p => hasPermission(p))
  }

  async function signOut() {
    await authProvider.signOut()
    adminUser.value = null
  }

  return {
    adminUser: computed(() => adminUser.value),
    isLoading: computed(() => isLoading.value),
    isAuthenticated,
    permissions,
    hasPermission,
    hasAnyPermission,
    fetchAdminUser,
    signOut
  }
}
